import { InjectionToken, type Signal } from '@angular/core';
import type { UploadProgressSignal } from './upload-progress.math';

export interface UploadFailureEntry {
  readonly name: string;
  readonly reason: string;
  /** Absent = retryable; `false` keeps the entry out of «Повторить всё». */
  readonly retryable?: boolean;
}

/**
 * What the shared upload bar needs from a product to draw itself. Each product implements it over
 * its own queue; the bar, its maths and the failure window stay one layer for all of them.
 */
export interface UploadProgressSource {
  /** The polled truth the progress model folds — see `nextUploadProgress`. */
  readonly snapshot: Signal<UploadProgressSignal>;
  readonly bytesSent: Signal<number>;
  readonly bytesTotal: Signal<number>;
  /** Previews confirmed ready, out of the files the server accepted. */
  readonly previewsReady: Signal<number>;
  readonly previewsTotal: Signal<number>;
  /**
   * Every in-flight file sits in a retry backoff — the connection is effectively gone. A product
   * with no retry layer has no stall state to report and says `false`: a file that cannot be
   * retried has already failed, and «Нет связи» over a queue that is not waiting would be a lie.
   */
  readonly stalled: Signal<boolean>;
  readonly failures: Signal<readonly UploadFailureEntry[]>;
  /** Clears the queue and abandons in-flight transfers. Never called while `stalled`. */
  cancel(): void;
  retryAll(): void;
}

export const UPLOAD_PROGRESS_SOURCE = new InjectionToken<UploadProgressSource>('UPLOAD_PROGRESS_SOURCE');
